import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, FileDown, Loader2 } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const IncomeReportMain = () => {
  const [incomes, setIncomes] = useState([]);
  const [stats, setStats] = useState({ total_amount: 0, total_count: 0, by_category: {}, by_mode: {} });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [collegeInfo, setCollegeInfo] = useState(null);

  useEffect(() => {
    fetchReport();
    fetchCollegeInfo();
  }, [currentPage, itemsPerPage]);

  const fetchCollegeInfo = async () => {
    try {
      const response = await apiClient.get('/api/tenants/my-college/settings');
      setCollegeInfo(response.data);
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      if (startDate) params.append('from_date', startDate);
      if (endDate) params.append('to_date', endDate);

      const response = await apiClient.get(`/api/accounts/incomes?${params.toString()}`);

      // apiClient interceptor unwraps {data: [...]} → response.data = [...], response.pagination = {...}
      if (Array.isArray(response.data)) {
        setIncomes(response.data);
        const pagination = response.pagination || {};
        setTotalItems(pagination.total || response.data.length);
        setStats(pagination.stats || { total_amount: 0, total_count: 0, by_category: {}, by_mode: {} });
      } else {
        // Fallback if interceptor didn't fire
        setIncomes(response.data?.data || []);
        setTotalItems(response.data?.total || 0);
        setStats(response.data?.stats || { total_amount: 0, total_count: 0, by_category: {}, by_mode: {} });
      }
    } catch (error) {
      console.error('Error:', error);
      setIncomes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    setCurrentPage(1);
    fetchReport();
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (val) => { setItemsPerPage(val); setCurrentPage(1); };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('page', 1);
      params.append('limit', 5000);
      if (startDate) params.append('from_date', startDate);
      if (endDate) params.append('to_date', endDate);

      const response = await apiClient.get(`/api/accounts/incomes?${params.toString()}`);
      const allData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const paginationStats = (response.pagination?.stats) || stats;

      if (allData.length === 0) { alert('No data to export'); return; }

      const pdfStats = {
        'Total Income': `Rs. ${(paginationStats.total_amount || 0).toFixed(2)}`,
        'Total Transactions': paginationStats.total_count || allData.length,
        'Report Period': startDate && endDate ? `${startDate} to ${endDate}` : 'All Time'
      };

      const columns = [
        { header: 'Date', dataKey: 'income_date' },
        { header: 'Category', dataKey: 'category' },
        { header: 'Title', dataKey: 'title' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Payment Mode', dataKey: 'payment_mode' },
        { header: 'Description', dataKey: 'description' }
      ];

      const data = allData.map(inc => ({
        income_date: inc.income_date || 'N/A',
        category: inc.category || 'N/A',
        title: inc.title || 'N/A',
        amount: `Rs. ${(inc.amount || 0).toFixed(2)}`,
        payment_mode: inc.payment_mode || 'N/A',
        description: inc.description || ''
      }));

      exportSummaryReportToPDF('Income Report', pdfStats, columns, data, 'income_report.pdf', collegeInfo);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  return (
    <div className="p-6 space-y-6" data-testid="income-report-page">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <CardTitle>Income Report</CardTitle>
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" data-testid="income-start-date" />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" data-testid="income-end-date" />
            </div>
            <Button onClick={handleGenerate} disabled={loading} data-testid="income-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {loading ? 'Loading...' : 'Generate'}
            </Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={totalItems === 0 || exporting} data-testid="income-export-pdf-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Income</p>
              <p className="text-2xl font-bold text-green-600" data-testid="income-total-amount">₹{(stats.total_amount || 0).toFixed(2)}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="income-total-count">{stats.total_count || totalItems}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-purple-600">{Object.keys(stats.by_category || {}).length}</p>
            </div>
          </div>

          {/* Category & Mode Breakdowns */}
          {(Object.keys(stats.by_category || {}).length > 0 || Object.keys(stats.by_mode || {}).length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {Object.keys(stats.by_category || {}).length > 0 && (
                <div>
                  <h3 className="text-base font-semibold mb-3">Income by Category</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.by_category).map(([category, amount]) => (
                      <div key={category} className="flex justify-between p-3 border rounded-lg">
                        <span className="text-gray-700">{category}</span>
                        <span className="font-bold text-green-600">₹{(amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Object.keys(stats.by_mode || {}).length > 0 && (
                <div>
                  <h3 className="text-base font-semibold mb-3">Income by Payment Mode</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.by_mode).map(([mode, amount]) => (
                      <div key={mode} className="flex justify-between p-3 border rounded-lg">
                        <span className="text-gray-700">{mode}</span>
                        <span className="font-bold text-blue-600">₹{(amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Income Table */}
          <h3 className="text-base font-semibold mb-3">Income Transactions</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="income-report-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 text-sm font-semibold">SL</th>
                  <th className="text-left p-3 text-sm font-semibold">Date</th>
                  <th className="text-left p-3 text-sm font-semibold">Category</th>
                  <th className="text-left p-3 text-sm font-semibold">Title</th>
                  <th className="text-left p-3 text-sm font-semibold">Amount</th>
                  <th className="text-left p-3 text-sm font-semibold">Payment Mode</th>
                  <th className="text-left p-3 text-sm font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center p-8 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading...</td></tr>
                ) : incomes.length === 0 ? (
                  <tr><td colSpan="7" className="text-center p-8 text-gray-500">No income records found</td></tr>
                ) : (
                  incomes.map((income, idx) => (
                    <tr key={income.id || idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className="p-3 text-sm">{income.income_date || '-'}</td>
                      <td className="p-3 text-sm">{income.category || '-'}</td>
                      <td className="p-3 text-sm">{income.title || '-'}</td>
                      <td className="p-3 text-sm font-semibold text-green-600">₹{(income.amount || 0).toFixed(2)}</td>
                      <td className="p-3 text-sm">{income.payment_mode || '-'}</td>
                      <td className="p-3 text-sm text-gray-600">{income.description || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IncomeReportMain;
